import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { DroppableProvided, DroppableStateSnapshot } from 'react-beautiful-dnd';

import Item from './Item';
import { Effect } from '../../@types/Models';
import { KanbanColumn, KanbanItem, Objective } from '../../@types/State';
import { effects } from '../../Effects';

interface Props {
  column: KanbanColumn;
  colInd: number;
  theme: Theme;
  selected: KanbanItem | null;
  handleCollapse: (
    item: KanbanItem
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  handleItemChange: (e: any, colIndex: number, itemInd: number) => void;
  deleteOp: (op: string, selected: KanbanItem | null) => void;
  deleteObjective: (op: Objective) => void;
  deleteColumnOp: (op: string, el: KanbanColumn) => void;
  toggleProvinceDrawer: () => void;
  openColumnDrawer: (el: KanbanColumn) => void;
  handleColumnChange: (e: any, i: number) => void;
  provided: DroppableProvided;
  snapshot: DroppableStateSnapshot;
  isWarPlan: boolean;
  deleteCol: (colInd: number) => void;
}

const KanbanColumnArea: React.FC<Props> = ({
  column,
  colInd,
  theme,
  selected,
  toggleProvinceDrawer,
  openColumnDrawer,
  handleCollapse,
  handleItemChange,
  deleteOp,
  deleteObjective,
  deleteColumnOp,
  handleColumnChange,
  provided,
  snapshot,
  isWarPlan,
  deleteCol,
}) => {
  return (
    <Box
      className='droppable-box'
      ref={provided.innerRef}
      sx={{
        bgcolor: snapshot.isDraggingOver
          ? 'palette.action.hover'
          : 'background.paper',
        minWidth: 180,
        maxWidth: '100vw',
        minHeight: 100,
        border: 1,
        padding: 1,
        borderColor: 'grey.800',
        display: 'flex',
        flexDirection: 'column',
      }}
      {...provided.droppableProps}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex' }}>
          <TextField
            variant='standard'
            name={`col_${colInd}`}
            fullWidth
            value={column.title}
            placeholder='Intel Group Name'
            onChange={(e) => handleColumnChange(e, colInd)}
            size='small'
            disabled={column.title === 'Unassigned'}
            sx={{ marginBottom: 0 }}
          />
          {(column.title !== 'Unassigned' || colInd > 0) && (
            <DeleteIcon
              onClick={() => deleteCol(colInd)}
              color='primary'
              style={{ cursor: 'pointer' }}
            />
          )}
        </Box>

        {column.items.map((item, itemInd) => (
          <Item
            key={item.id}
            item={item}
            itemInd={itemInd}
            column={column}
            colInd={colInd}
            theme={theme}
            selected={selected}
            isWarPlan={isWarPlan}
            handleCollapse={handleCollapse}
            handleItemChange={handleItemChange}
            deleteOp={deleteOp}
            deleteObjective={deleteObjective}
            toggleDrawer={toggleProvinceDrawer}
          />
        ))}
      </Box>
      {column.requiredOps && column.requiredOps.length > 0 && (
        <Box mt={1}>
          <Typography
            variant='caption'
            style={{ marginTop: '5px' }}
            textAlign='center'
            component={'div'}
          >
            Group Ops:
          </Typography>
          <Stack
            direction='row'
            sx={{
              maxWidth: '249px',
              flexWrap: 'wrap',
            }}
          >
            {column.requiredOps?.map((op: string, i: number) => {
              const effect: Effect = effects[op];
              const numb = i + 1;
              const isRowEnd = numb % 4 === 0;
              return (
                <Tooltip
                  placement='top'
                  title={`Delete ${effect.displayName} from ${column.title} Group`}
                  key={effect.shortName}
                  arrow
                >
                  <Chip
                    key={effect.shortName}
                    label={effect.shortName}
                    variant='outlined'
                    onDelete={() => deleteColumnOp(op, column)}
                    size='small'
                    sx={{
                      marginTop: '5px',
                      marginRight: isRowEnd ? 0 : '3px',
                      width: '60px',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}
      {isWarPlan && column.title !== 'Unassigned' && (
        <Box sx={{ alignSelf: 'flex-start', justifySelf: 'flex-end' }}>
          <Button
            color='primary'
            aria-label='add objective'
            onClick={() => openColumnDrawer(column)}
            variant='outlined'
            size='small'
            sx={{ mt: 1 }}
          >
            Add OPs to Group
          </Button>
        </Box>
      )}
      {provided.placeholder}
    </Box>
  );
};

export default KanbanColumnArea;
